<template>
  <div class="swap-rate">
    1&nbsp;{{ rateTokens.symbol.value }}&nbsp;=&nbsp;
    <Transition :duration="500" appear mode="out-in" name="text">
      <span :key="`rate-${swapStore.isUpdatingRate}`">
        {{ convertRateTokens.formatted.value }}
      </span></Transition
    >&nbsp;({{ $t('common.includingFee') }})
  </div>

  <div class="swap-table">
    <div class="swap-table__row">
      <div class="swap-table__label">
        {{ $t('addLiquidity.expectedOutput') }}
      </div>
      <div class="swap-table__value">
        <Transition :duration="300" appear mode="out-in" name="text">
          <span :key="`output-${outputTokens.formatted.value}`">{{
            outputTokens.formatted.value
          }}</span>
        </Transition>
      </div>
    </div>
    <div v-if="hasSlippage" class="swap-table__row is-small">
      <div class="swap-table__label">
        {{
          swapStore.lastInteractiveField === 'from'
            ? $t('burn.minimumReceived')
            : $t('transaction.maximumSent')
        }}
        {{ $t('transaction.afterSlippage') }} (<span
          :class="{ warning: slippageWarning }"
          >{{
            (swapStore.slippage ? swapStore.slippage * 100 : 0).toFixed(2)
          }}%</span
        >)
      </div>
      <div class="swap-table__value">
        <Transition :duration="300" appear mode="out-in" name="text">
          <span :key="`slippage-${slippageOutputTokens.formatted.value}`">{{
            slippageOutputTokens.formatted.value
          }}</span>
        </Transition>
      </div>
    </div>
    <div v-if="slippageWarning" class="swap-table__row is-small warning">
      <PInlineMessage class="mt-1 mb-1 slippage-warning" severity="warn">{{
        $t('errors.transactionMayFail')
      }}</PInlineMessage>
    </div>
    <div class="swap-table__row is-small">
      <div class="swap-table__label">{{ $t('common.fees') }}</div>
      <div class="swap-table__value">
        <Transition :duration="300" appear mode="out-in" name="text">
          <span :key="`fee-${swapStore.feeInUsd}`">
            ~{{ swapStore.feeInUsd }}$
          </span>
        </Transition>
      </div>
    </div>
  </div>

  <div
    v-if="swapStore.lastInteractiveField === 'from'"
    class="swap-help"
    v-html="
      $t('addLiquidity.outputIsEstimatedAtLeast', [
        slippageOutputTokens.formatted.value,
      ])
    "
  />
  <div
    v-else
    class="swap-help"
    v-html="
      $t('addLiquidity.outputIsEstimatedNoMore', [
        slippageOutputTokens.formatted.value,
      ])
    "
  />
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import { useSwapStore } from '@/store';
import { useCurrencyFormat } from '@/composables/useCurrencyFormat';
import { getCurve } from '@/utils/contracts';
import { VERSION_0, VERSION_0_5 } from '@/constants';

const swapStore = useSwapStore();

const version = computed(() => swapStore.version);
const fromToken = computed(() => swapStore.fromCurrency.token);
const toToken = computed(() => swapStore.toCurrency.token);
const slippageValue = computed(() => swapStore.slippage);

const slippageAmount = computed(() => swapStore.slippageAmount);
const hasSlippage = computed(
  () =>
    (version.value === VERSION_0 &&
      swapStore.curve === getCurve('unstable', version.value)) ||
    version.value === VERSION_0_5,
);
const convertRate = computed(() => swapStore.convertRate); // Mul 100 - hotfix
const slippageToken = computed(() =>
  swapStore.lastInteractiveField === 'from' ? toToken.value : fromToken.value,
);

const rateTokens = useCurrencyFormat(1, fromToken);
const outputTokens = useCurrencyFormat(
  computed(() => swapStore.toCurrency.amount),
  toToken,
);
const slippageOutputTokens = useCurrencyFormat(slippageAmount, slippageToken);
const convertRateTokens = useCurrencyFormat(convertRate, toToken);
// TODO: fix magic number
const slippageWarning = computed(
  () => hasSlippage.value && slippageValue.value < 0.005,
);
</script>

<style lang="scss" scoped>
.swap-rate {
  padding: 16px 16px;
  font-size: 17px;
  font-weight: 500;
  color: var(--text-color);
}

.swap-table {
  border: 1px solid transparentize(#e5e4fa, 0.85) !important;
  border-radius: 16px;
  overflow: hidden;

  &__row {
    padding: 12px 16px;
    display: flex;
    flex-direction: row;
    align-items: center;
    color: var(--text-color);
    font-size: 17px;
    border-left: none;
    border-right: none;

    &.is-small {
      opacity: 0.7;
      font-size: 13px;
      font-weight: 500;
    }

    &.warning {
      padding: 0;
      opacity: 1;
    }

    &:not(:last-child) {
      border-bottom: 1px solid transparentize(#e5e4fa, 0.85);
    }
    &:last-child {
      border-top: 1px solid transparentize(#e5e4fa, 0.85);
    }
  }

  &__label {
    width: 50%;
  }

  &__value {
    margin-left: auto;
  }
}

.swap-help {
  color: transparentize(#e5e4fa, 0.6);
  font-size: 13px;
  font-weight: 500;
  margin-top: 24px;
  padding: 0 16px;
  line-height: 18px;

  b {
    color: #e5e4fa;
    font-weight: 500;
  }
}

.slippage-warning {
  width: 90%;
  margin: 0 auto;
}

.warning {
  padding: 0;
  color: #cc8925;
}

.text-enter-from,
.text-leave-to {
  opacity: 0.7;
  filter: grayscale(1);
}

.text-enter-to,
.text-leave-from {
  opacity: 1;
}

.text-enter-active,
.text-leave-active,
.text-move {
  transition: all 0.5s ease-out;
}
</style>
